'use client'
import { SlugTranslationsQuery } from '.generated/dato'
import { Link, Locale, Pathname, usePathname } from 'i18n/routing'
import { useLocale, useTranslations } from 'next-intl'
import { useParams, useSearchParams } from 'next/navigation'
import { createContext, useContext } from 'react'
import { AnimatedUnderline, SlimUnderline, WideUnderline } from './Underline'
import { Menu } from '@base-ui/react/menu'
import { getSlugTranslations } from 'lib/slugTranslations'

const SlugTranslationsContext = createContext<SlugTranslationsQuery | null>(
  null,
)

export const SlugTranslationsProvider = SlugTranslationsContext.Provider

export function LanguageSwitcher() {
  const pathname = usePathname()
  const locale = useLocale() as Locale
  const params = useParams<any>()
  const searchParams = useSearchParams()
  const slugTranslations = useContext(SlugTranslationsContext)
  const t = useTranslations('nav')

  return (
    <>
      <div className="relative block lg:hidden">
        <Link
          href={{
            pathname,
            params: mapParams(slugTranslations, locale, 'de', pathname, params),
            query: Object.fromEntries(searchParams.entries()),
          }}
          locale="de"
          className="relative text-20! overflow-hidden pb-12"
        >
          DE
          <span className="absolute bottom-[0.4rem] left-0 right-0">
            <AnimatedUnderline isActive={locale === 'de'}>
              <SlimUnderline />
            </AnimatedUnderline>
          </span>
        </Link>
        {' / '}
        <Link
          href={{
            pathname,
            params: mapParams(slugTranslations, locale, 'en', pathname, params),
            query: Object.fromEntries(searchParams.entries()),
          }}
          locale="en"
          className="relative text-20! overflow-hidden pb-12"
        >
          EN
          <span className="absolute bottom-[0.4rem] left-0 right-0">
            <AnimatedUnderline isActive={locale === 'en'}>
              <SlimUnderline />
            </AnimatedUnderline>
          </span>
        </Link>
      </div>
      <div className="hidden lg:block">
        <Menu.Root>
          <Menu.Trigger
            aria-label="Language Switcher"
            className="flex items-center gap-x-6"
          >
            {locale.toUpperCase()} <ChevronDownIcon />
          </Menu.Trigger>
          <Menu.Portal>
            <Menu.Positioner sideOffset={24}>
              <Menu.Popup className="flex flex-col gap-y-20 border-[1px] bg-white border-lightGray px-24 py-20">
                <Menu.Item className="relative group text-center text-black">
                  <Link
                    href={{
                      pathname,
                      params: mapParams(
                        slugTranslations,
                        locale,
                        'de',
                        pathname,
                        params,
                      ),
                      query: Object.fromEntries(searchParams.entries()),
                    }}
                    locale="de"
                  >
                    {t('german')}
                    <span className="absolute bottom-0 left-0 right-0">
                      <AnimatedUnderline isActive={locale === 'de'}>
                        <WideUnderline className="text-black" />
                      </AnimatedUnderline>
                    </span>
                  </Link>
                </Menu.Item>
                <Menu.Item className="relative group text-center text-black">
                  <Link
                    href={{
                      pathname,
                      params: mapParams(
                        slugTranslations,
                        locale,
                        'en',
                        pathname,
                        params,
                      ),
                      query: Object.fromEntries(searchParams.entries()),
                    }}
                    locale="en"
                  >
                    {t('english')}
                    <span className="absolute bottom-0 left-0 right-0">
                      <AnimatedUnderline isActive={locale === 'en'}>
                        <WideUnderline className="text-black" />
                      </AnimatedUnderline>
                    </span>
                  </Link>
                </Menu.Item>
              </Menu.Popup>
            </Menu.Positioner>
          </Menu.Portal>
        </Menu.Root>
      </div>
    </>
  )
}

/**
 * There is one scenario where we need to map the params when switching the language:
 * When the current entry type in DatoCMS has internationalization enabled for the slug-field
 *
 * In this case we need to know the translation for the current slug to the target locale when
 * switching the language.
 *
 * Since this is the case only for the skill pages right now, we just pass along all the skill
 * page slug mappings for the different languages on every request for the root layout.
 *
 * This function is used to look up the correct translation for the slug when switching the
 * language on a skill page. Take a look at the shape of SlugTranslationQuery to see why
 * we need all the `find`-calls.
 */
function mapParams(
  slugTranslations: SlugTranslationsQuery | null,
  sourceLocale: Locale,
  targetLocale: Locale,
  pathname: Extract<Pathname, { pathname: unknown }>['pathname'],
  params: any,
) {
  if (sourceLocale === targetLocale) return params

  switch (pathname) {
    case '/kompetenzen/[slug]':
      return {
        ...params,
        slug: getSlugTranslations({
          items: slugTranslations?.allSkillPages,
          sourceLocale,
          slug: params.slug,
          targetLocale,
        }),
      }

    case '/blog/[slug]':
      return {
        ...params,
        slug: getSlugTranslations({
          items: slugTranslations?.allBlogPosts,
          sourceLocale,
          slug: params.slug,
          targetLocale,
        }),
      }

    case '/[slug]':
      return {
        ...params,
        slug: getSlugTranslations({
          items: slugTranslations?.allLandingPages,
          sourceLocale,
          slug: params.slug,
          targetLocale,
        }),
      }

    default:
      return params
  }
}

function ChevronDownIcon() {
  return (
    <svg
      width="9"
      height="6"
      viewBox="0 0 9 6"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M9 0H0L4.5 6L9 0Z" fill="black" />
    </svg>
  )
}
